/**
 * Bloqueio biométrico (Face ID / Touch ID / digital) — camada única.
 *
 * Nativo (iOS/Android): usa o plugin `capacitor-native-biometric` via import
 * dinâmico com `@vite-ignore`, para que o bundle web nunca dependa dele.
 * Web/PWA: não há biometria disponível de forma confiável — o recurso é
 * exibido como indisponível e o app continua funcionando normalmente.
 */
import { isNative } from "@/lib/platform";

export interface BiometricAvailability {
  available: boolean;
  /** Rótulo em pt-BR do método disponível (ex.: "Face ID"). */
  label: string;
  reason?: string;
}

type BiometricPlugin = {
  isAvailable: () => Promise<{ isAvailable: boolean; biometryType?: number; errorCode?: string }>;
  verifyIdentity: (opts: {
    reason?: string;
    title?: string;
    subtitle?: string;
    description?: string;
    negativeButtonText?: string;
  }) => Promise<void>;
};

const BIOMETRY_LABELS: Record<number, string> = {
  0: "Biometria",
  1: "Touch ID",
  2: "Face ID",
  3: "Impressão digital",
  4: "Reconhecimento facial",
  5: "Íris",
};

async function loadPlugin(): Promise<BiometricPlugin | null> {
  if (!isNative()) return null;
  try {
    const moduleName = "capacitor-native-biometric";
    const mod: any = await import(/* @vite-ignore */ moduleName).catch(() => null);
    return (mod?.NativeBiometric as BiometricPlugin) ?? null;
  } catch {
    return null;
  }
}

export async function getBiometricAvailability(): Promise<BiometricAvailability> {
  if (!isNative()) {
    return {
      available: false,
      label: "Biometria",
      reason: "O bloqueio por biometria só está disponível no aplicativo instalado (iOS/Android).",
    };
  }
  const plugin = await loadPlugin();
  if (!plugin) {
    return {
      available: false,
      label: "Biometria",
      reason: "Este dispositivo não expõe um sensor biométrico compatível.",
    };
  }
  try {
    const res = await plugin.isAvailable();
    return {
      available: !!res.isAvailable,
      label: BIOMETRY_LABELS[res.biometryType ?? 0] ?? "Biometria",
      reason: res.isAvailable
        ? undefined
        : "Nenhuma biometria cadastrada neste aparelho. Configure Face ID, Touch ID ou digital nos ajustes do sistema.",
    };
  } catch {
    return { available: false, label: "Biometria", reason: "Não foi possível consultar o sensor biométrico." };
  }
}

/**
 * Pede a confirmação biométrica. Retorna `true` quando o usuário se autentica.
 * Textos em pt-BR, explícitos sobre o motivo da solicitação.
 */
export async function verifyBiometric(reason = "Confirme que é você para abrir o Zappy."): Promise<boolean> {
  const plugin = await loadPlugin();
  if (!plugin) return false;
  try {
    await plugin.verifyIdentity({
      reason,
      title: "Zappy protegido",
      subtitle: "Desbloqueio biométrico",
      description: reason,
      negativeButtonText: "Cancelar",
    });
    return true;
  } catch {
    return false;
  }
}
